// ─────────────────────────────────────────────────────────────────────────────
// RF-037 — La racha cuenta PERÍODOS, y el período lo fija Gestión: semanas o meses.
//
// `StreakDto.semanasActuales` y `record` conservan su nombre por compatibilidad del contrato, pero
// con periodicidad mensual cuentan MESES. Todo lo que rotule esos números —el widget, el Perfil, los
// umbrales de las insignias— tiene que preguntar aquí en vez de escribir "semanas" a mano, o la
// pantalla del empleado dice una cosa y el cierre hace otra.
// ─────────────────────────────────────────────────────────────────────────────
import type { StreakPeriodicity } from '@/api/recognition';

/** Claves de plural de un umbral, en las tres formas que exige `weeksPlural`. */
export interface StreakUnitKeys {
  one: string;
  few: string;
  many: string;
}

export interface StreakUnitCopy {
  /**
   * Etiqueta corta junto al contador grande. Va en las TRES formas de plural porque en español y
   * portugués "mes"/"mês" no es invariante como la abreviatura "sem": sin esto la portada pintaba
   * "3 mes".
   */
  countKeys: StreakUnitKeys;
  /** Título del contador en el Perfil ("Semanas de racha" / "Meses de racha"). */
  streakLabel: string;
  /** Aviso de racha en riesgo, redactado para el período. */
  atRisk: string;
  /** Subtítulo del widget ("¡Reconoce cada semana…" / "…cada mes…"). */
  subtitle: string;
  /** Umbral de una insignia, en las tres formas de plural. */
  badgeKeys: StreakUnitKeys;
  /** Récord del Wrapped ("Tu racha más larga: N semanas / N meses"), en las tres formas. */
  wrappedKeys: StreakUnitKeys;
  /** Pie del KPI de racha en Innovación. */
  kpiHint: string;
  /** RF-036 — Progreso del período abierto ("2 de 3 esta semana"), aún sin alcanzar el mínimo. */
  periodProgress: string;
  /** RF-036 — Mismo progreso, ya cumplido el mínimo del período. */
  periodProgressDone: string;
  /** RF-036 — Pie del contador cuando la racha incluye el período abierto, pendiente de cierre. */
  provisionalNote: string;
}

const WEEKLY: StreakUnitCopy = {
  countKeys: { one: 'streak.weeks', few: 'streak.weeks', many: 'streak.weeks' },
  streakLabel: 'profile.weeksStreak',
  atRisk: 'streak.atRisk',
  subtitle: 'streak.subtitle',
  badgeKeys: { one: 'streak.badgeWeek', few: 'streak.badgeWeeks', many: 'streak.badgeWeeksMany' },
  wrappedKeys: { one: 'wrapped.streakValueOne', few: 'wrapped.streakValue', many: 'wrapped.streakValueMany' },
  kpiHint: 'innovacion.kpiStreakHint',
  periodProgress: 'streak.periodProgress',
  periodProgressDone: 'streak.periodProgressDone',
  provisionalNote: 'streak.provisionalNote',
};

const MONTHLY: StreakUnitCopy = {
  countKeys: { one: 'streak.month', few: 'streak.months', many: 'streak.monthsMany' },
  streakLabel: 'profile.monthsStreak',
  atRisk: 'streak.atRiskMonthly',
  subtitle: 'streak.subtitleMonthly',
  badgeKeys: { one: 'streak.badgeMonth', few: 'streak.badgeMonths', many: 'streak.badgeMonthsMany' },
  wrappedKeys: { one: 'wrapped.streakValueMonthOne', few: 'wrapped.streakValueMonth', many: 'wrapped.streakValueMonthMany' },
  kpiHint: 'innovacion.kpiStreakHintMonthly',
  periodProgress: 'streak.periodProgressMonthly',
  periodProgressDone: 'streak.periodProgressDoneMonthly',
  provisionalNote: 'streak.provisionalNoteMonthly',
};

/**
 * Textos que corresponden a la periodicidad vigente. Semanal es el valor por omisión y el que se
 * aplica cuando el backend no envía el campo (respuesta anterior a RF-037).
 */
export function streakUnitCopy(periodicidad: StreakPeriodicity | null | undefined): StreakUnitCopy {
  return periodicidad === 'monthly' ? MONTHLY : WEEKLY;
}
